import {
  EpcLoanActionTypes,
  FieldAccessRule,
  LoanAccessRule,
  PersonaAccessResponse,
} from '@frontend/epc';
import { getRequestLockActionType } from '../../../../common/app-helpers';
import {
  RateRequestStatus,
  RequestActionType,
  RequestLockStatus,
} from '../../../../common/constants/enumTypes';

const epcLoanActionMap = {
  NewLock: EpcLoanActionTypes.LockRequest,
  ActiveNewLock: EpcLoanActionTypes.LockRequest,
  ActiveLockUpdate: EpcLoanActionTypes.RePrice,
  ExpiredReLock: EpcLoanActionTypes.Relock,
  CancelledReLock: EpcLoanActionTypes.Relock,
};

export const getTPOActionAccessibility = (
  allowedActions,
  requestActionType,
) => {
  const defaultValue = 'ENABLED';
  let filterItem: any[] = [];
  if (allowedActions) {
    filterItem = allowedActions.filter(
      (item) => item.action === requestActionType,
    );
  }
  return filterItem ? filterItem[0]?.accessibility : defaultValue;
};

export const getPersonaActionAccessibility = (
  allowedActions: PersonaAccessResponse[],
  requestActionType: EpcLoanActionTypes,
): string => {
  const defaultValue = 'ENABLE';
  let filterItem: PersonaAccessResponse[] = [];
  if (allowedActions) {
    filterItem = allowedActions.filter(
      (item) => item.loanAction === requestActionType,
    );
  }
  return filterItem ? filterItem[0]?.restrictionType : defaultValue;
};

export const REQUEST_LOCK_FIELD_ID = 'Button_requestlock';
export const LOCK_REQUEST_TOOL_SECTION = 'LockRequestToolAccess';

const FIELD_ACCESS_HIDE = ['hidden'];
const FIELD_ACCESS_DISABLE = [
  'readonly',
  'read only',
  'viewonly',
  'view only',
  'disabled',
];

const getFieldAccess = (
  fieldAccessRules: FieldAccessRule[] | undefined,
  fieldId: string,
) =>
  fieldAccessRules
    ?.find((rule) => rule?.fieldId?.toLowerCase() === fieldId.toLowerCase())
    ?.access?.toLowerCase();

export const isFieldAccessHidden = (
  fieldAccessRules: FieldAccessRule[] | undefined,
  fieldId: string,
) =>
  FIELD_ACCESS_HIDE.includes(getFieldAccess(fieldAccessRules, fieldId) || '');

export const isFieldAccessDisabled = (
  fieldAccessRules: FieldAccessRule[] | undefined,
  fieldId: string,
) =>
  FIELD_ACCESS_DISABLE.includes(
    getFieldAccess(fieldAccessRules, fieldId) || '',
  );

const getLoanAccess = (
  loanAccessRules: LoanAccessRule[] | undefined,
  section: string,
) =>
  loanAccessRules
    ?.find((rule) => rule?.section?.toLowerCase() === section.toLowerCase())
    ?.access?.toLowerCase();

export const isLoanAccessHidden = (
  loanAccessRules: LoanAccessRule[] | undefined,
  section: string,
) =>
  FIELD_ACCESS_HIDE.includes(getLoanAccess(loanAccessRules, section) || '');

export const isLoanAccessDisabled = (
  loanAccessRules: LoanAccessRule[] | undefined,
  section: string,
) =>
  FIELD_ACCESS_DISABLE.includes(getLoanAccess(loanAccessRules, section) || '');

export const isRequestLockButtonVisible = (
  applicationName,
  loanData,
  allowedActions,
  fieldAccessRules?: FieldAccessRule[],
  loanAccessRules?: LoanAccessRule[],
) => {
  let isVisible = true;
  if (
    applicationName &&
    applicationName === 'TPO' &&
    getTPOActionAccessibility(allowedActions, 'LockRequest') === 'HIDDEN'
  ) {
    isVisible = false;
  }

  const requestLockActionType = getRequestLockActionType(loanData);
  /**
   * unfortunately the request lock button is used for multiple operations
   * so based on the request lock action type we need to determine if the permission applies to lock action type
   */
  if (requestLockActionType) {
    const epcActionType: EpcLoanActionTypes =
      epcLoanActionMap[requestLockActionType];
    if (
      epcActionType &&
      getPersonaActionAccessibility(allowedActions, epcActionType) === 'Hide'
    ) {
      isVisible = false;
    }
  }

  if (isFieldAccessHidden(fieldAccessRules, REQUEST_LOCK_FIELD_ID)) {
    isVisible = false;
  }

  if (isLoanAccessHidden(loanAccessRules, LOCK_REQUEST_TOOL_SECTION)) {
    isVisible = false;
  }

  return isVisible;
};

export const isSaveToLoanButtonVisible = (
  applicationName,
  allowedActions,
  tpo,
  clientSettingsData,
) => {
  const hideLockRequest = clientSettingsData?.hideLockRequest;
  if (hideLockRequest) {
    return false;
  }

  if (applicationName && applicationName === 'TPO') {
    const isHidden =
      getTPOActionAccessibility(allowedActions, 'FloatLock') === 'HIDDEN';
    if (isHidden || tpo?.underwritingDelegated || hideLockRequest) return false;
  }
  return true;
};

export const isRequestLockButtonDisabled = (
  rateStale,
  loanData,
  applicationName,
  allowedActions,
  isDisableRequestLockButton,
  shutDownAllowLockRequestValidation,
  disableLock,
  fieldAccessRules?: FieldAccessRule[],
  loanAccessRules?: LoanAccessRule[],
) => {
  if (disableLock) {
    return true;
  }

  const { requestLockStatus, requestAction } = loanData;
  const isAllowLockRequest = shutDownAllowLockRequestValidation();
  if (
    applicationName &&
    applicationName === 'TPO' &&
    getTPOActionAccessibility(allowedActions, 'LockRequest') === 'DISABLED'
  ) {
    return true;
  }
  const isDisabled =
    (requestLockStatus === RequestLockStatus.NoStatus &&
      requestAction === RequestActionType.ValidateReLock) ||
    (requestLockStatus === RequestLockStatus.NotLocked &&
      requestAction === RequestActionType.ValidateReLock) ||
    (requestLockStatus === RequestLockStatus.Expired &&
      requestAction === RequestActionType.QuoteRequest) ||
    (requestLockStatus === RequestLockStatus.Cancelled &&
      requestAction === RequestActionType.QuoteRequest) ||
    isDisableRequestLockButton ||
    isAllowLockRequest ||
    isRequestLockDisabledByPersonaRules(
      loanData,
      allowedActions,
      fieldAccessRules,
      loanAccessRules,
    );

  return isDisabled;
};

export const isRequestLockDisabledByPersonaRules = (
  loanData,
  allowedActions,
  fieldAccessRules?: FieldAccessRule[],
  loanAccessRules?: LoanAccessRule[],
) => {
  const requestLockActionType = getRequestLockActionType(loanData);
  const epcActionType = requestLockActionType
    ? epcLoanActionMap[requestLockActionType]
    : null;
  /**
   * unfortunately the request lock button is used for multiple operations
   * so based on the request lock action type we need to determine if the permission applies to lock action type
   */
  const isPersonaDisabled =
    epcActionType &&
    getPersonaActionAccessibility(allowedActions, epcActionType) === 'Disable';

  return (
    !!isPersonaDisabled ||
    isFieldAccessDisabled(fieldAccessRules, REQUEST_LOCK_FIELD_ID) ||
    isLoanAccessDisabled(loanAccessRules, LOCK_REQUEST_TOOL_SECTION)
  );
};

export const isFloatButtonVisible = (
  allowedActions: PersonaAccessResponse[],
  clientSettingsData,
) => {
  let isFloatButtonVisible = clientSettingsData?.icePpeFloat;
  if (
    getPersonaActionAccessibility(
      allowedActions,
      EpcLoanActionTypes.FloatLock,
    ) === 'Hide'
  ) {
    isFloatButtonVisible = false;
  }

  return isFloatButtonVisible;
};

export const isFloatButtonDisabled = (
  allowedActions: PersonaAccessResponse[],
  originalLoanInformation,
) => {
  const { lockRequests, rateLock } = originalLoanInformation || {};

  const lockRequestIsDenied = lockRequests?.every(
    (req) => req.requestedStatus === 'Denied',
  );

  const isDisabledFromLock = lockRequests?.length && !lockRequestIsDenied;
  const isDisabledFromPersona =
    getPersonaActionAccessibility(
      allowedActions,
      EpcLoanActionTypes.FloatLock,
    ) === 'Disable';
  const validRequestStatus = [
    RateRequestStatus.NotLockedNoRequest,
    RateRequestStatus.Cancelled,
    RateRequestStatus.ExpiredNoRequest,
  ];
  const isEnabledByRequestStatus = validRequestStatus.includes(
    rateLock?.rateRequestStatus,
  );
  // we need to enable the button if the request status matches one of the valid statuses
  // this will take precedence over the other disabled conditions
  if (isEnabledByRequestStatus) return false;

  const isDisabledByRequestStatus = !isEnabledByRequestStatus;
  return (
    isDisabledByRequestStatus || isDisabledFromLock || isDisabledFromPersona
  );
};

// Returns true when Enable Auto Float is on, the app is not TPO, and persona and lock state allow Float.
export const shouldAutoFloat = (
  clientSettingsData,
  applicationName,
  allowedActions: PersonaAccessResponse[],
  originalLoanInformation,
) =>
  clientSettingsData?.enableAutoFloat === true &&
  applicationName !== 'TPO' &&
  getPersonaActionAccessibility(
    allowedActions,
    EpcLoanActionTypes.FloatLock,
  ) !== 'Hide' &&
  !isFloatButtonDisabled(allowedActions, originalLoanInformation);

export const isSaveToLoanButtonDisabled = (
  loanData,
  applicationName,
  allowedActions,
  disableLock,
) => {
  if (disableLock) {
    return true;
  }
  if (applicationName && applicationName === 'TPO') {
    const isDisabled =
      getTPOActionAccessibility(allowedActions, 'FloatLock') === 'DISABLED';
    if (isDisabled) return true;
    const { requestLockStatus, requestAction } = loanData;
    return (
      requestAction === RequestActionType.ValidateReLock &&
      [RequestLockStatus.Expired, RequestLockStatus.Cancelled].includes(
        requestLockStatus,
      )
    );
  }
  return false;
};

export const getEPCActionType = (
  requestLockActionType,
  autolocked,
  wcpPolicyApplied?,
  updateLockDate?,
) => {
  const { buyside, sellSideBestPrice, sellSideSameProduct } = autolocked;

  if (buyside || sellSideBestPrice || sellSideSameProduct) {
    return 'LOCK_CONFIRM';
  }

  if (
    requestLockActionType === 'NewLock' ||
    requestLockActionType === 'ActiveNewLock'
  ) {
    return 'LOCK';
  }

  if (requestLockActionType === 'ActiveLockUpdate') {
    if (wcpPolicyApplied && updateLockDate) {
      return 'UPDATE';
    }
    return 'RELOCK';
  }

  return 'RELOCK';
};

export const checkLockRequested = (loanData, lockRequests) => {
  const { requestAction, requestLockStatus } = loanData;
  const hasLockRequest = lockRequests?.some(
    (request) => request.requestedStatus === 'Requested',
  );
  return (
    hasLockRequest &&
    requestAction === RequestActionType.QuoteRequest &&
    requestLockStatus === RequestLockStatus.NotLocked
  );
};
